import { ESdChangeReason } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IWithSDProps, withSD } from "../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../shared/pure-sd";
import { KModal } from "../ui-kit/k-modal/k-modal";
import "./default-modal.scss";

export interface IDefaultModalState {}

export interface IDefaultModalProps {
  children?: React.ReactNode;
  showModal?: boolean;
  showSecondModal?: boolean;
  closeModal?: () => void;
  closeSecondModal?: () => void;
  firstHeader?: React.ReactNode;
  secondHeader?: React.ReactNode;
  /** Truthy picks `firstHeader`, any other defined value picks `secondHeader`. */
  headerCondition?: unknown;
  width?: number | string;
  centered?: boolean;
  isLoading?: boolean;
  bodyStyle?: React.CSSProperties;
  className?: string;
  dontRenderSpinner?: boolean;
  /**
   * The dialog's decisions, in `KModal`'s own footer slot rather than at the end of the
   * body — so they stay put while the fields scroll. Pass `KModalFooter`.
   *
   * The slot is a sibling of the body, never a descendant, so a submit button in here is
   * outside any `<form>` above it: give the form an `id` and the button `submitForm`.
   */
  footer?: React.ReactNode;
}

/**
 * The designer's dialog. Eighty surfaces open one, sixty-seven of them the Config
 * Manager's settings dialogs, so this is where "every modal looks the same" is decided.
 *
 * It draws `KModal` now rather than antd's. What that changes is the chrome: the header,
 * the close control and the spinner come from the ui-kit and resolve tokens, where this
 * used to paint its own close glyph in `#246EFF` and hand the body to antd's Modal — which
 * is why every one of these dialogs stayed light while the page behind it followed the
 * theme.
 *
 * The prop surface is unchanged on purpose. `firstHeader` / `secondHeader` /
 * `headerCondition` and the second-modal pair are how the callers already speak, and
 * rewriting eighty of them to say it differently would be a much larger change than the
 * one actually being made.
 */
class DefaultModal extends PureSdComponent<IDefaultModalProps & IWithSDProps, IDefaultModalState> {
  /** Which of the two headers the dialog is showing. */
  getHeader = (): React.ReactNode => {
    const { headerCondition, firstHeader, secondHeader } = this.props;
    if (headerCondition === undefined || headerCondition === null) return firstHeader;
    return headerCondition === true ? firstHeader : secondHeader;
  };

  handleClose = (): void => {
    // Which dialog is open decides which close runs — the callers that use the second one
    // keep both mounted and flip between them.
    if (this.props.showModal) {
      this.props.closeModal?.();
      return;
    }
    this.props.closeSecondModal?.();
  };

  render() {
    return (
      <KModal
        className={`kuika__modal ${this.props.className || ""}`}
        header={this.getHeader()}
        showModal={this.props.showModal || this.props.showSecondModal}
        closeModal={this.handleClose}
        // `KModal` defaults to 400 and centered; these are this component's own defaults,
        // which its callers have been laid out against.
        width={this.props.width ?? 460}
        centered={this.props.centered ?? false}
        bodyStyle={this.props.bodyStyle}
        footer={this.props.footer}
        isLoading={this.props.isLoading}
        dontRenderSpinner={this.props.dontRenderSpinner}
        closeTestId="closeModal"
      >
        {this.props.children}
      </KModal>
    );
  }

  /**
   * A screen-design change repaints eighty dialogs if this says yes, so it says yes only for
   * what this component actually draws.
   *
   * Open/close is not the whole of that. The footer is a node the caller rebuilds on its own
   * state — the stateful save moves it from idle to loading to ✓ with the dialog sitting
   * still — and `isLoading` is the caller's spinner. Both arrive as changed props during a
   * screen-design change that is otherwise none of this dialog's business, and comparing them
   * by identity keeps the original bargain: a caller that did not re-render hands back the
   * same nodes, and this still declines.
   */
  checkNeedRender = (changeReason: ESdChangeReason, nextProps: IDefaultModalProps & IWithSDProps): boolean => {
    return (
      this.props.showModal !== nextProps.showModal ||
      this.props.showSecondModal !== nextProps.showSecondModal ||
      this.props.isLoading !== nextProps.isLoading ||
      this.props.footer !== nextProps.footer
    );
  };
}

const defaultModal = withSD<IDefaultModalProps>(DefaultModal);
export { defaultModal as DefaultModal };
